import { useClipboard } from '@vueuse/core';
import { appName } from '~/constants';
import messages from '~~/i18n/locale/pt_br/share-collection';

import { CopyToastDescription } from '~/components/CopyToastDescription';
import { useToast } from '~/components/ui/toast';

/**
 * Composable for sharing collection functionality
 */
export function useShareCollection(
	wishlistId: Ref<string>,
	isOwner: Ref<boolean> = ref(true),
	ownerName: Ref<string> = ref('')
) {
	const { copy } = useClipboard();
	const { toast } = useToast();
	const shareUrl = computed(() => {
		const baseUrl = useRuntimeConfig().public.ORIGIN;
		const url = new URL(
			`${baseUrl}/colecoes/compartilhada/${wishlistId.value}`
		);

		// Add UTM parameters
		url.searchParams.set('utm_source', 'shared');
		url.searchParams.set('utm_medium', 'share');
		url.searchParams.set('utm_campaign', 'collection');

		return url.toString();
	});

	const shareMessage = computed(() => {
		const textKey = isOwner.value ? 'owner' : 'visitor';
		let message = messages?.shareCollection?.[textKey]?.shareMessage ?? '';

		// Replace placeholders
		message = message.replace('{shareUrl}', shareUrl.value);

		// Always replace owner name if available
		if (ownerName.value) {
			message = message.replace(/{ownerName}/g, ownerName.value);
		}

		return message;
	});

	const encodedShareMessage = computed(() =>
		encodeURIComponent(shareMessage.value)
	);

	const encodedShareUrl = computed(() => encodeURIComponent(shareUrl.value));

	const shareData = computed(() => ({
		title: 'Compartilhe sua coleção',
		text: shareMessage.value
	}));

	function handleNativeShare() {
		if (navigator?.share && navigator?.canShare(shareData.value)) {
			navigator.share(shareData.value);
		} else {
			copy(shareUrl.value);
			toast({
				description: h(CopyToastDescription)
			});
		}
	}

	return {
		shareUrl,
		shareMessage,
		encodedShareMessage,
		encodedShareUrl,
		shareData,
		handleNativeShare
	};
}
